import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type { EmployeeEarningKind, EmployeeSummary } from '@aeris/contracts'
import { amountToString, parseAmount } from '@aeris/finance'
import { ZERO_MONEY, add, formatMoney } from '@aeris/money'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  Field,
  InlineAlert,
  Input,
  MoneyInput,
  Select,
  Spinner,
} from '@aeris/ui'

import { periodLabel, shiftPeriod } from '../lib/api/commissions.ts'
import {
  createEmployee,
  fetchEmployees,
  updateEmployee,
} from '../lib/api/expenses.ts'
import { ApiError } from '../lib/api.ts'
import {
  EARNING_HINTS,
  EARNING_KINDS,
  EARNING_LABELS,
} from '../lib/payroll-labels.ts'

/**
 * Os funcionários da folha (F146), com salário E bônus (F157): sem login.
 *
 * *"tipo funcionário tem o salário + bônus de 500"*. O salário deixou de ser
 * **o** número e virou um ganho entre quatro; todos com a mesma história.
 *
 * Mudar qualquer um deles não reescreve o passado: o valor novo vale a partir
 * do mês escolhido, e os meses de antes continuam com o que valia neles. É o
 * que faz a folha de janeiro continuar batendo com o que foi pago em janeiro.
 *
 * O bônus **de uma vez só** não está aqui: aqui é o que se repete todo mês. O
 * pontual se lança na folha, no mês em que aconteceu.
 */

const money = (wire: string): string => formatMoney(parseAmount(wire))

const monthOptions = (period: string): string[] =>
  [-1, 0, 1, 2].map((offset) => shiftPeriod(period, offset))

type Draft = {
  name: string
  /** Um campo por tipo de ganho; vazio é "não tem". */
  amounts: Record<EmployeeEarningKind, string>
  validFrom: string
}

const emptyAmounts = (): Record<EmployeeEarningKind, string> => ({
  salary: '',
  bonus: '',
  transport: '',
  meal: '',
})

/** O que está gravado hoje, campo a campo. */
const draftOf = (employee: EmployeeSummary, period: string): Draft => {
  const amounts = emptyAmounts()
  for (const earning of employee.earnings) {
    amounts[earning.kind] = earning.amount
  }
  return { name: employee.name, amounts, validFrom: period }
}

/** O que se manda ao servidor: o vazio não vira linha nenhuma. */
const valuesOf = (
  draft: Draft
): { kind: EmployeeEarningKind; amount: string }[] =>
  EARNING_KINDS.flatMap((kind) =>
    draft.amounts[kind] === ''
      ? []
      : [{ kind, amount: amountToString(parseAmount(draft.amounts[kind])) }]
  )

/** Mudou algum valor? Se não, não se escreve na história (F157). */
const changed = (draft: Draft, employee: EmployeeSummary): boolean =>
  EARNING_KINDS.some((kind) => {
    const antes = employee.earnings.find((row) => row.kind === kind)
    const agora = draft.amounts[kind]
    if (agora === '') return false
    return (
      antes === undefined ||
      amountToString(parseAmount(agora)) !==
        amountToString(parseAmount(antes.amount))
    )
  })

const EmployeeForm = ({
  initial,
  period,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
}: {
  initial: Draft
  period: string
  submitLabel: string
  pending: boolean
  onSubmit: (draft: Draft) => void
  onCancel?: () => void
}): ReactNode => {
  const [draft, setDraft] = useState(initial)
  const blocker =
    draft.name.trim() === ''
      ? 'Diga o nome.'
      : draft.amounts.salary === ''
        ? 'Informe o salário.'
        : null

  const total = valuesOf(draft).reduce(
    (soma, value) => add(soma, parseAmount(value.amount)),
    ZERO_MONEY
  )

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault()
        if (blocker === null) onSubmit(draft)
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Nome" required>
          {(field) => (
            <Input
              {...field}
              value={draft.name}
              maxLength={120}
              onChange={(event) => {
                setDraft({ ...draft, name: event.target.value })
              }}
            />
          )}
        </Field>
        <Field label="Vale a partir de">
          {(field) => (
            <Select
              {...field}
              value={draft.validFrom}
              onChange={(event) => {
                setDraft({ ...draft, validFrom: event.target.value })
              }}
            >
              {monthOptions(period).map((month) => (
                <option key={month} value={month}>
                  {periodLabel(month)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      {/*
        Os quatro num bloco só, porque é um valor só que a loja paga: o que ela
        deve a esta pessoa todo mês. Deixar em branco é não ter.
      */}
      <fieldset className="grid gap-3 sm:grid-cols-2">
        <legend className="sr-only">Quanto recebe por mês</legend>
        {EARNING_KINDS.map((kind) => (
          <Field
            key={kind}
            label={EARNING_LABELS[kind]}
            hint={EARNING_HINTS[kind]}
            required={kind === 'salary'}
          >
            {(field) => (
              <MoneyInput
                {...field}
                value={draft.amounts[kind]}
                onValueChange={(amount) => {
                  setDraft({
                    ...draft,
                    amounts: { ...draft.amounts, [kind]: amount },
                  })
                }}
              />
            )}
          </Field>
        ))}
      </fieldset>

      <div className="flex items-center justify-end gap-2">
        <span className="mr-auto text-(length:--text-xs) text-(--text-muted)">
          {blocker ?? `Por mês: ${formatMoney(total)}`}
        </span>
        {onCancel === undefined ? null : (
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            Cancelar
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={blocker !== null}
          loading={pending}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}

export const EmployeesDialog = ({
  period,
  onClose,
}: {
  period: string
  onClose: () => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const employees = useQuery({
    queryKey: ['expenses', 'employees', period],
    queryFn: ({ signal }) => fetchEmployees(period, signal),
  })

  const failed = (cause: unknown): void => {
    setError(
      cause instanceof ApiError ? cause.message : 'Não foi possível salvar.'
    )
  }
  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['expenses'] })
  }

  const create = useMutation({
    mutationFn: async (draft: Draft) =>
      await createEmployee({
        name: draft.name.trim(),
        earnings: valuesOf(draft),
        validFrom: draft.validFrom,
      }),
    onSuccess: async () => {
      setError(null)
      setCreating(false)
      await refresh()
    },
    onError: failed,
  })

  const update = useMutation({
    mutationFn: async (input: {
      employee: EmployeeSummary
      draft?: Draft
      status?: 'active' | 'archived'
    }) => {
      const { employee, draft } = input
      return await updateEmployee(employee.id, {
        version: employee.version,
        name: draft?.name.trim() ?? employee.name,
        note: employee.note,
        status: input.status ?? employee.status,
        earnings:
          draft !== undefined && changed(draft, employee)
            ? { validFrom: draft.validFrom, values: valuesOf(draft) }
            : null,
      })
    },
    onSuccess: async () => {
      setError(null)
      setEditing(null)
      await refresh()
    },
    onError: failed,
  })

  const rows = employees.data?.employees ?? []

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title="Funcionários"
        description="Quem entra na folha e quanto recebe por mês. Só o nome: não precisa ter login."
        className="max-w-2xl"
      >
        <div className="flex flex-col gap-4">
          {creating ? (
            <div className="rounded-(--radius-md) border border-(--border-subtle) p-3">
              <EmployeeForm
                initial={{
                  name: '',
                  amounts: emptyAmounts(),
                  validFrom: period,
                }}
                period={period}
                submitLabel="Cadastrar"
                pending={create.isPending}
                onSubmit={(draft) => {
                  create.mutate(draft)
                }}
                onCancel={() => {
                  setCreating(false)
                }}
              />
            </div>
          ) : (
            <Button
              variant="secondary"
              className="self-start"
              onClick={() => {
                setCreating(true)
                setEditing(null)
              }}
            >
              Novo funcionário
            </Button>
          )}

          {error === null ? null : (
            <InlineAlert tone="danger">{error}</InlineAlert>
          )}

          {employees.isPending ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-(length:--text-sm) text-(--text-muted)">
              Nenhum funcionário cadastrado ainda.
            </p>
          ) : (
            <ul
              aria-label="Funcionários da loja"
              className="flex flex-col divide-y divide-(--border-subtle) rounded-(--radius-md) border border-(--border-subtle)"
            >
              {rows.map((employee) => (
                <li key={employee.id} className="flex flex-col gap-2 px-3 py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-(--weight-medium)">
                      {employee.name}
                    </span>
                    {employee.status === 'archived' ? (
                      <Badge tone="neutral">Arquivado</Badge>
                    ) : null}
                    <span className="ml-auto flex flex-col items-end">
                      <span className="font-(--font-mono) text-(length:--text-sm) tabular-nums">
                        {employee.earnings.length === 0
                          ? 'sem salário neste mês'
                          : formatMoney(
                              employee.earnings.reduce(
                                (soma, row) =>
                                  add(soma, parseAmount(row.amount)),
                                ZERO_MONEY
                              )
                            )}
                      </span>
                      {/*
                        A composição, e só quando há mais de uma parcela: quem
                        só tem salário não precisa ler "Salário 2.000,00" ao
                        lado de "2.000,00".
                      */}
                      {employee.earnings.length > 1 ? (
                        <span className="text-(length:--text-xs) text-(--text-muted)">
                          {employee.earnings
                            .map(
                              (row) =>
                                `${EARNING_LABELS[row.kind]} ${money(row.amount)}`
                            )
                            .join(' + ')}
                        </span>
                      ) : null}
                    </span>
                    {editing === employee.id ? null : (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Editar ${employee.name}`}
                        onClick={() => {
                          setEditing(employee.id)
                          setCreating(false)
                        }}
                      >
                        Editar
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      loading={
                        update.isPending &&
                        update.variables.employee.id === employee.id &&
                        update.variables.status !== undefined
                      }
                      onClick={() => {
                        update.mutate({
                          employee,
                          status:
                            employee.status === 'active'
                              ? 'archived'
                              : 'active',
                        })
                      }}
                    >
                      {employee.status === 'active' ? 'Arquivar' : 'Reativar'}
                    </Button>
                  </div>
                  {editing === employee.id ? (
                    <EmployeeForm
                      initial={draftOf(employee, period)}
                      period={period}
                      submitLabel="Salvar"
                      pending={update.isPending}
                      onSubmit={(draft) => {
                        update.mutate({ employee, draft })
                      }}
                      onCancel={() => {
                        setEditing(null)
                      }}
                    />
                  ) : null}
                  {employee.history.length > employee.earnings.length ? (
                    <span className="text-(length:--text-xs) text-(--text-muted)">
                      {employee.history
                        .slice(0, 3)
                        .map(
                          (row) =>
                            `${EARNING_LABELS[row.kind]} ${money(row.amount)} desde ${periodLabel(row.validFrom)}`
                        )
                        .join(' · ')}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
